// PhaseProps.ts — shared interfaces for the 12 M2 phase templates.
//
// Source of truth: docs/spec_v3/fe_spec/FE_SPEC.md §3.
// 12 phase closed-set: framing / exploration / narrowing / challenging /
//                      validation / synthesis / listening / reflection /
//                      decomposition / recomposition / stress_testing / mediation.
//
// FE-A (ui_visual_forge · M2): visual layer only. State stores live in
// FE-B (page_state_orchestrator) — do not mutate session.ts here.

import type { PersonaId } from '@/store/session';

export interface PhaseSpeech {
  persona_id: PersonaId;
  body: string; // accumulated chunks (speech_chunk delta_text concatenation)
  is_final: boolean;
  monotonic_id_last: number;
}

export interface PhaseIronKill {
  candidate_id: string;
  reason: string;
  monotonic_id: number;
  // ★ P0-5 闭环: cited_evidence — array of attachment_id strings.
  // BE wire format: { iron_kill: { ..., cited_evidence: ['attachment_1', ...] } }
  cited_evidence?: string[];
}

// 4 sections (PRD §2.5.9): key_facts / key_data / contradictions / gaps.
export interface AttachmentBriefingDTO {
  available: boolean;
  key_facts_count: number;
  key_data_count: number;
  contradictions_count: number;
  gaps_count: number;
  char_count: number;
  raw_path: string; // /data/briefings/{session_id}.md
  quality_compromised: boolean;
  // Inline content from POST /sessions 200 (P0-1 闭环): FE 不另起 fetch.
  content?: string | null;
}

export type IronTone = 'standard' | 'compassionate_critique' | null;

// 12 phase closed-set (PRD §2.5.4) — keep as a runtime constant for switch
// safety + tests.
export const PHASE_NAMES = [
  'framing',
  'exploration',
  'narrowing',
  'challenging',
  'validation',
  'synthesis',
  'listening',
  'reflection',
  'decomposition',
  'recomposition',
  'stress_testing',
  'mediation',
] as const;

export type PhaseName = (typeof PHASE_NAMES)[number];

export interface PhaseProps {
  phase_name: PhaseName | string; // canonical 12; string fallback for unknown.
  speakers: PersonaId[];          // from choreography.phases[].speakers.
  speeches: PhaseSpeech[];        // accumulated per-speaker buffers.
  iron_kills: PhaseIronKill[];    // only `challenging` consumes; others get [].
  phase_summary: string | null;   // sage_condenser ≤500 chars, paused preview.
  attachment_briefing: AttachmentBriefingDTO | null;
  iron_tone: IronTone;
}
